import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
  Divider,
  Badge,
  MenuItem,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import { CustomDrawer } from '@/components/common';
import {
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  // Add as AddIcon,
  // Remove as RemoveIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/types';
import {
  fetchActivityLogs,
  fetchCompanies,
  fetchMaterials,
} from '@/store';
import moment from 'moment';

export const MaterialLogs: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activityLogs, activityLogsLoading, pagination } = useSelector(
    (state: RootState) => state.activityLog
  );
  const { companies } = useSelector((state: RootState) => state.company);
  const { materials } = useSelector((state: RootState) => state.material);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter states
  const [materialIdFilter, setMaterialIdFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [companyIdFilter, setCompanyIdFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [poNumberFilter, setPoNumberFilter] = useState<string>('');
  const [documentNumberFilter, setDocumentNumberFilter] = useState<string>('');
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('');

  // Details expansion state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const loadData = useCallback(() => {
    const params: any = {
      page: page + 1,
      limit: rowsPerPage,
    };

    if (materialIdFilter) params.materialId = materialIdFilter;
    if (typeFilter) params.type = typeFilter;
    if (startDateFilter) params.startDate = new Date(startDateFilter).toISOString();
    if (endDateFilter) params.endDate = new Date(endDateFilter).toISOString();
    if (companyIdFilter) params.companyId = companyIdFilter;
    if (levelFilter) params.level = parseInt(levelFilter);
    if (poNumberFilter) params.poNumber = poNumberFilter;
    if (documentNumberFilter) params.documentNumber = documentNumberFilter;
    if (jobTypeFilter) params.jobType = jobTypeFilter;

    dispatch(fetchActivityLogs(params));
  }, [
    dispatch,
    page,
    rowsPerPage,
    materialIdFilter,
    typeFilter,
    startDateFilter,
    endDateFilter,
    companyIdFilter,
    levelFilter,
    poNumberFilter,
    documentNumberFilter,
    jobTypeFilter,
  ]);

  const loadRelatedData = useCallback(() => {
    dispatch(fetchCompanies({ limit: 100 }));
    dispatch(fetchMaterials({ limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadRelatedData();
  }, [loadRelatedData]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setMaterialIdFilter('');
    setTypeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setCompanyIdFilter('');
    setLevelFilter('');
    setPoNumberFilter('');
    setDocumentNumberFilter('');
    setJobTypeFilter('');
    setPage(0);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (materialIdFilter) count++;
    if (typeFilter) count++;
    if (startDateFilter) count++;
    if (endDateFilter) count++;
    if (companyIdFilter) count++;
    if (levelFilter) count++;
    if (poNumberFilter) count++;
    if (documentNumberFilter) count++;
    if (jobTypeFilter) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'created':
        return 'success';
      case 'updated':
        return 'warning';
      case 'workprocess':
        return 'info';
      case 'deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return 'N/A';
    const date = moment(dateTimeString).local();
    return date.isValid() ? date.format('DD/MM/YYYY hh:mm A') : 'Invalid Date';
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderChangeIndicator = (change: number) => {
    if (change > 0) {
      return (
        <Chip
          // icon={<AddIcon />}
          label={`+${change}`}
          color="success"
          size="small"
          variant="outlined"
        />
      );
    } else if (change < 0) {
      return (
        <Chip
          // icon={<RemoveIcon />}
          label={change}
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<TrendingFlatIcon />}
        label="0"
        color="default"
        size="small"
        variant="outlined"
      />
    );
  };

  const renderDetailPanel = (log: any) => {
    return (
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={expandedRows.has(log._id)} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3, bgcolor: 'grey.50', my: 2 }}>
              <Grid container spacing={3}>
                {/* Material Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Material Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Material Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {log.materialName || log.materialId?.materialName || 'N/A'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Material Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {log.materialId?.materialType || 'N/A'}
                  </Typography>
                </Grid>

                {/* Quantity Changes */}
                {(log.previous || log.changes) && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                        Quantity Changes
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Metric</strong></TableCell>
                              <TableCell align="right"><strong>Previous</strong></TableCell>
                              <TableCell align="center"><strong>Change</strong></TableCell>
                              <TableCell align="right"><strong>Current</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Initial Count</TableCell>
                              <TableCell align="right">{log.previous?.initialCount ?? log.initialCount}</TableCell>
                              <TableCell align="center">-</TableCell>
                              <TableCell align="right">{log.initialCount}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Initial Weight (Kg)</TableCell>
                              <TableCell align="right">{log.previous?.initialKg ?? log.initialKg}</TableCell>
                              <TableCell align="center">-</TableCell>
                              <TableCell align="right">{log.initialKg}</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'info.lighter' }}>
                              <TableCell><strong>Available Count</strong></TableCell>
                              <TableCell align="right"><strong>{log.previous?.quantityCount ?? log.quantityCount}</strong></TableCell>
                              <TableCell align="center">
                                {log.changes?.quantityCount && renderChangeIndicator(log.changes.quantityCount)}
                              </TableCell>
                              <TableCell align="right"><strong>{log.quantityCount}</strong></TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'info.lighter' }}>
                              <TableCell><strong>Available Weight (Kg)</strong></TableCell>
                              <TableCell align="right"><strong>{log.previous?.quantityKg ?? log.quantityKg}</strong></TableCell>
                              <TableCell align="center">
                                {log.changes?.quantityKg && renderChangeIndicator(log.changes.quantityKg)}
                              </TableCell>
                              <TableCell align="right"><strong>{log.quantityKg}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </>
                )}

                {/* Work Process Information */}
                {log.type === 'workprocess' && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                        Work Process Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Level
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        Level {log.level} - {log.levelName}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        PO Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="primary">
                        {log.poNumber || log.poId?.poNumber || 'N/A'}
                      </Typography>
                    </Grid>

                    {log.documentNumber && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Document Number
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {log.documentNumber}
                        </Typography>
                      </Grid>
                    )}

                    {log.jobType && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Job Type
                        </Typography>
                        <Chip
                          label={log.jobType.toUpperCase()}
                          color={log.jobType === 'jobwork' ? 'warning' : 'info'}
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    )}

                    {log.startDateTime && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Work Process Start Time
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatDateTime(log.startDateTime)}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* Audit Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Audit Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Created Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2} p={2} sx={{ bgcolor: 'success.lighter', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="success.dark" fontWeight="bold" gutterBottom>
                      Created By
                    </Typography>
                    {log.createdBy && (
                      <>
                        <Typography variant="body2" fontWeight="medium">
                          {log.createdBy.name || 'N/A'}
                        </Typography>
                        {log.createdBy.empId && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Emp ID: {log.createdBy.empId}
                          </Typography>
                        )}
                        {/* {log.createdBy.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email: {log.createdBy.email}
                          </Typography>
                        )} */}
                        {/* {log.createdBy.mobile && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Mobile: {log.createdBy.mobile}
                          </Typography>
                        )} */}
                      </>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="success.dark" fontWeight="bold" display="block">
                      {formatDateTime(log.createdAt)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Updated Information */}
                {log.updatedBy && log.createdAt !== log.updatedAt && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box mb={2} p={2} sx={{ bgcolor: 'warning.lighter', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="warning.dark" fontWeight="bold" gutterBottom>
                        Last Updated By
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {log.updatedBy.name || 'N/A'}
                      </Typography>
                      {log.updatedBy.empId && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Emp ID: {log.updatedBy.empId}
                        </Typography>
                      )}
                      {/* {log.updatedBy.email && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Email: {log.updatedBy.email}
                        </Typography>
                      )}
                      {log.updatedBy.mobile && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Mobile: {log.updatedBy.mobile}
                        </Typography>
                      )} */}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="warning.dark" fontWeight="bold" display="block">
                        {formatDateTime(log.updatedAt)}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Remarks */}
                {log.remarks && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                        Remarks
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.100' }}>
                        <CardContent>
                          <Typography variant="body2">
                            {log.remarks}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Material Activity Logs
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Filter Button */}
      <Grid container spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mb: 3 }}>
        <Grid size="auto">
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Grid>
      </Grid>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.lighter' }}>
              <TableCell width={60}></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Material</strong></TableCell>
              <TableCell><strong>Level</strong></TableCell>
              <TableCell><strong>PO Number</strong></TableCell>
              <TableCell><strong>Document Number</strong></TableCell>
              <TableCell><strong>Job Type</strong></TableCell>
              <TableCell><strong>Company</strong></TableCell>
              <TableCell align="right"><strong>Change (Count)</strong></TableCell>
              <TableCell align="right"><strong>Change (Kg)</strong></TableCell>
              <TableCell><strong>Date & Time</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activityLogsLoading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : activityLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No activity logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              activityLogs.map((log) => (
                <React.Fragment key={log._id}>
                  <TableRow hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(log._id)}
                        sx={{
                          transform: expandedRows.has(log._id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.type.toUpperCase()}
                        color={getTypeColor(log.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {log.materialName || log.materialId?.materialName || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.materialId?.materialType || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.level ? (
                        <Typography variant="body2">
                          L{log.level} - {log.levelName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        {log.poNumber || log.poId?.poNumber || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.documentNumber || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.jobType ? (
                        <Chip
                          label={log.jobType.toUpperCase()}
                          color={log.jobType === 'jobwork' ? 'warning' : 'info'}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.companyId?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {log.changes?.quantityCount !== undefined ? (
                        renderChangeIndicator(log.changes.quantityCount)
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {log.changes?.quantityKg !== undefined ? (
                        renderChangeIndicator(log.changes.quantityKg)
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(log.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {renderDetailPanel(log)}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={pagination?.totalDocs || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Filter Drawer */}
      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter Activity Logs"
        width={'100%'}
        anchor="right"
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            startIcon={<FilterIcon />}
            fullWidth
          >
            Clear All Filters
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            options={materials}
            getOptionLabel={(option) => `${option.materialName} (${option.materialType})`}
            value={materials.find(m => m._id === materialIdFilter) || null}
            onChange={(_, newValue) => setMaterialIdFilter(newValue?._id || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material"
                variant="outlined"
                size="small"
                placeholder="Select material..."
              />
            )}
          />

          <TextField
            select
            label="Activity Type"
            variant="outlined"
            size="small"
            fullWidth
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="created">Created</MenuItem>
            <MenuItem value="updated">Updated</MenuItem>
            <MenuItem value="workprocess">Work Process</MenuItem>
          </TextField>

          <TextField
            label="PO Number"
            variant="outlined"
            size="small"
            fullWidth
            value={poNumberFilter}
            onChange={(e) => setPoNumberFilter(e.target.value)}
            placeholder="e.g., PO-2024-001"
          />

          <TextField
            label="Document Number"
            variant="outlined"
            size="small"
            fullWidth
            value={documentNumberFilter}
            onChange={(e) => setDocumentNumberFilter(e.target.value)}
            placeholder="e.g., DOC-ABC123"
          />

          <TextField
            select
            label="Job Type"
            variant="outlined"
            size="small"
            fullWidth
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
          >
            <MenuItem value="">All Job Types</MenuItem>
            <MenuItem value="jobwork">Jobwork</MenuItem>
            <MenuItem value="purchase">Purchase</MenuItem>
          </TextField>

          <TextField
            select
            label="Company"
            variant="outlined"
            size="small"
            fullWidth
            value={companyIdFilter}
            onChange={(e) => setCompanyIdFilter(e.target.value)}
          >
            <MenuItem value="">All Companies</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company._id} value={company._id}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Work Process Level"
            variant="outlined"
            size="small"
            fullWidth
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <MenuItem value="">All Levels</MenuItem>
            <MenuItem value="1">Level 1 - Punching Laser</MenuItem>
            <MenuItem value="2">Level 2 - Bending Machine</MenuItem>
            <MenuItem value="3">Level 3 - Welding & Grinding</MenuItem>
            <MenuItem value="4">Level 4 - Powder Coating</MenuItem>
            <MenuItem value="5">Level 5 - Assembly</MenuItem>
          </TextField>

          <TextField
            type="date"
            label="Start Date"
            variant="outlined"
            size="small"
            fullWidth
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          <TextField
            type="date"
            label="End Date"
            variant="outlined"
            size="small"
            fullWidth
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          {activeFilterCount > 0 && (
            <Box mt={2} p={2} sx={{ bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}
        </Box>
      </CustomDrawer>
    </Box>
  );
};
